export type Metric = {
  id: string;
  label: string;
  category: string;
  sovDomain: string | null;       // "SOV-1" through "SOV-8" or null
  frameworkTags: string[];         // ["C3A", "EU-CSF"]
  question: string;
  description?: string | null;
  weightDefault: number;
};

export type Provider = {
  id: string;
  name: string;
  scores: Record<string, number | undefined>;
  reasons?: Record<string, string | undefined>;
  subjectToCloudAct?: boolean;
};

export type ProviderScore = {
  id: string;
  name: string;
  score: number;
};

export type InfraType = 'onprem' | 'private' | 'hybrid' | 'public';

export type QuestionnaireStage = 'providers' | 'cloud-act' | 'it-landscape' | 'questions-cloud-usage' | 'questions';

export type ViewType = "bars" | "radar" | "heatmap";

export type RadarData = {
  radarData: any[];
  rankedProviders: Provider[];
  sortedMetrics: Metric[];
};

// Sovereignty profile definition
export type SovereigntyProfile = {
  label: string;
  description: string;
  domainWeights: Record<string, number>;  // "SOV-1" through "SOV-8" → weight
  notes?: Record<string, string>;
};

// SEAL level definition
export type SealLevelDef = {
  label: string;
  minScore: number;
  maxScore: number;
  description: string;
};

// Loaded sovereignty profiles data
export type SovereigntyProfilesData = {
  profiles: Record<string, SovereigntyProfile>;
  sealLevels: {
    levels: Record<string, SealLevelDef>;
  };
  metricToSovMapping: Record<string, string[]>;
  frameworkModes?: Record<string, FrameworkModeDef>;
  hardRequirements?: Record<string, HardRequirement[]>;
};

// Computed result per provider
export type ProviderSovereigntyResult = {
  id: string;
  name: string;
  sovereigntyScore: number;         // 0–10
  sovereigntyScorePercent: number;   // 0–100 (with bonus)
  domainScores: Record<string, number>;  // SOV-1..SOV-8 → 0–10
  sealLevels: Record<string, number>;    // SOV-1..SOV-8 → 0–4
  overallSealLevel: number;              // 0–4
  businessScore: number;                 // 0–10
  disqualified: boolean;                 // Cloud Act disqualification
  hardRequirementFailures?: string[];    // metric labels that failed
};

// Framework mode type (3 assessment modes)
export type FrameworkMode = "NO_FRAMEWORK_C5" | "EU-CSF" | "C3A";

// Profile ID type (backward compat alias)
export type ProfileId = FrameworkMode;

// Framework mode definition from sovereignty_profiles.json
export type FrameworkModeDef = {
  label: string;
  labelEn?: string;
  description: string;
  descriptionEn?: string;
  requires?: Record<string, number>;
  scoring?: string;
  usesProfile?: string;
  usesSEAL?: boolean;
  usesHardRequirements?: boolean;
};

// Hard requirement definition
export type HardRequirement = {
  metric: string;
  minScore: number;
  label: string;
};

// SOV Domain identifiers
export const SOV_DOMAINS = [
  "SOV-1", "SOV-2", "SOV-3", "SOV-4",
  "SOV-5", "SOV-6", "SOV-7", "SOV-8"
] as const;

export type SovDomain = typeof SOV_DOMAINS[number];
